import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Row, Col, FormGroup, Label, Input, Button, Spinner, Alert, Table
} from 'reactstrap'
import api from '../../../services/api'

const PROPERTY_OPTIONS = [
  { value: 'CompanyName', label: 'Company name' },
  { value: 'Department', label: 'Department' },
  { value: 'OfficeLocation', label: 'Office' },
  { value: 'City', label: 'City' },
  { value: 'JobTitle', label: 'Job title' },
]

const ContactSiteMapping = () => {
  const { companyId } = useParams()

  const [enabled, setEnabled] = useState(false)
  const [sourceProperty, setSourceProperty] = useState('')
  const [mappings, setMappings] = useState([])
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [configRes, sitesRes] = await Promise.all([
        api.get(`/company/${companyId}/contact-site-mapping/config`),
        api.get(`/company/${companyId}/contact-site-mapping/sites`),
      ])

      if (configRes.ok && sitesRes.ok) {
        const config = await configRes.json()
        const sitesData = await sitesRes.json()

        setEnabled(config.enabled)
        setSourceProperty(config.sourceProperty || '')
        setMappings(config.mappings || [])
        setSites(sitesData)
      } else {
        setError('Failed to load configuration')
      }
    } catch (e) {
      setError('Failed to load configuration')
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const loadDistinctValues = async (property) => {
    if (!property) {
      setMappings([])
      return
    }

    try {
      setError(null)
      const res = await api.get(
        `/company/${companyId}/contact-site-mapping/distinct-values?property=${property}`
      )

      if (res.ok) {
        const values = await res.json()
        setMappings(prevMappings => {
          return values.map(value => {
            const existing = prevMappings.find(
              m => m.graphPropertyValue?.toLowerCase() === value.toLowerCase()
            )
            return existing || { graphPropertyValue: value, connectWiseSiteId: null, connectWiseSiteName: null }
          })
        })
      } else {
        setError('Failed to load distinct values')
      }
    } catch (e) {
      setError('Failed to load distinct values')
    }
  }

  const handlePropertyChange = (e) => {
    const newProperty = e.target.value
    setSourceProperty(newProperty)
    setMappings([])
    loadDistinctValues(newProperty)
  }

  const handleSiteChange = (index, siteId) => {
    setMappings(prev => {
      const updated = [...prev]
      if (siteId === '') {
        updated[index] = { ...updated[index], connectWiseSiteId: null, connectWiseSiteName: null }
      } else {
        const site = sites.find(s => s.id === parseInt(siteId))
        updated[index] = {
          ...updated[index],
          connectWiseSiteId: parseInt(siteId),
          connectWiseSiteName: site ? site.name : null,
        }
      }
      return updated
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      const payload = {
        enabled,
        sourceProperty: enabled ? sourceProperty : null,
        mappings: enabled ? mappings : [],
      }

      const res = await api.post(`/company/${companyId}/contact-site-mapping/config`, payload)

      if (res.ok) {
        setSuccess(true)
        setTimeout(() => setSuccess(false), 3000)
      } else {
        const text = await res.text()
        setError(text || 'Failed to save configuration')
      }
    } catch (e) {
      setError('Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">Configuration saved successfully</Alert>}

      <FormGroup check className="mb-3">
        <Label check>
          <Input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />{' '}
          Enable Contact Site Sync
        </Label>
      </FormGroup>

      {enabled && (
        <>
          <FormGroup>
            <Label for="sourceProperty">Graph Property</Label>
            <Input
              type="select"
              id="sourceProperty"
              value={sourceProperty}
              onChange={handlePropertyChange}
            >
              <option value="">-- Select Property --</option>
              {PROPERTY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
          </FormGroup>

          {sourceProperty && mappings.length > 0 && (
            <Table striped responsive className="mt-3">
              <thead>
                <tr>
                  <th>Graph Value</th>
                  <th>ConnectWise Site</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, index) => (
                  <tr key={mapping.graphPropertyValue}>
                    <td className="align-middle">{mapping.graphPropertyValue}</td>
                    <td>
                      <Input
                        type="select"
                        value={mapping.connectWiseSiteId || ''}
                        onChange={(e) => handleSiteChange(index, e.target.value)}
                      >
                        <option value="">-- Unmapped --</option>
                        {sites.map(site => (
                          <option key={site.id} value={site.id}>{site.name}</option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {sourceProperty && mappings.length === 0 && (
            <Alert color="info" className="mt-3">
              No distinct values found for this property. Run a sync first to populate user data.
            </Alert>
          )}
        </>
      )}

      <Row className="mt-3">
        <Col>
          <Button
            color="success"
            className="float-right"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <span>
                <Spinner style={{ height: '1.15rem', width: '1.15rem' }} color="light" /> Saving
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </Col>
      </Row>
    </div>
  )
}

export default ContactSiteMapping
